import { isBrowser } from "../environment";
import * as logger from "../logger";
import type { AnalyticsSession, DeviceMetadata, ResolvedConfig, TrackedEvent } from "../types";
import { EventQueue } from "./queue";
import { backoffDelay, classify, sleep } from "./retry";
import type { FlushReason } from "./scheduler";
import { FlushScheduler } from "./scheduler";
import type { WireRequest } from "./wire";
import { endSessionRequest, eventsRequest, sessionRequest } from "./wire";

/**
 * The seam. Everything the SDK knows about delivery lives behind this interface —
 * batching, persistence, retry and the wire format itself. track(), the session manager
 * and event construction never see any of it.
 */
export interface Transport {
	init(config: ResolvedConfig): void;
	startSession(session: AnalyticsSession, device: DeviceMetadata): void;
	endSession(sessionId: string, endedAt: string): void;
	enqueue(event: TrackedEvent): void;
}

type FlushTrigger = FlushReason | "size" | "init";

type SendResult = "ok" | "dropped" | "exhausted";

let config: ResolvedConfig | null = null;
let queue: EventQueue | null = null;
let scheduler: FlushScheduler | null = null;
/** Guards against overlapping batches, which retry backoff makes easy to hit. */
let flushing = false;
/** Whose end to report when the tab goes away. */
let currentSessionId: string | null = null;

function enabled(): boolean {
	return config !== null && config.endpoint !== "" && config.apiKey !== "" && isBrowser();
}

async function send(request: WireRequest, label: string, cfg: ResolvedConfig): Promise<SendResult> {
	for (let attempt = 0; ; attempt += 1) {
		let status: number | null = null;
		try {
			status = (await fetch(request.url, request.init)).status;
		} catch {
			status = null;
		}

		const verdict = classify(status);
		if (verdict === "ok") return "ok";

		if (verdict === "drop") {
			logger.warn(`${label}: rejected with ${String(status)}, dropping`);
			return "dropped";
		}

		if (attempt >= cfg.maxRetries) {
			logger.warn(`${label}: still failing after ${cfg.maxRetries} retries, giving up for now`);
			return "exhausted";
		}

		const delay = backoffDelay(attempt, cfg);
		logger.log(`${label}: ${status === null ? "network error" : `status ${status}`}, retrying in ${Math.round(delay)}ms`);
		await sleep(delay);
	}
}

/**
 * The page may be going away, so this is one attempt with `keepalive` and no retry loop.
 * If the page survives after all, a failure puts the batch back rather than losing it.
 *
 * `keepalive` rather than `navigator.sendBeacon`: the contract authenticates with a header,
 * and sendBeacon cannot set one.
 */
function flushOnHide(cfg: ResolvedConfig): void {
	if (currentSessionId !== null) {
		// Premature if the tab comes back, but the next hide overwrites it, and a tab that
		// never comes back would otherwise leave a session with no end at all.
		const ending = endSessionRequest(cfg, currentSessionId, new Date().toISOString(), true);
		void fetch(ending.url, ending.init).catch(() => undefined);
	}

	if (!queue || queue.size === 0) return;

	const batch = queue.take(cfg.maxQueueSize);
	const request = eventsRequest(cfg, batch, true);

	void fetch(request.url, request.init)
		.then(response => {
			if (classify(response.status) === "retry") queue?.requeue(batch);
		})
		.catch(() => queue?.requeue(batch));

	logger.log(`flushing ${batch.length} event(s) on hide, best effort`);
}

async function flush(trigger: FlushTrigger): Promise<void> {
	if (!enabled() || !queue || config === null) return;
	const cfg = config;

	// Deliberately not gated on `flushing`: the in-flight batch is already out of the queue,
	// and a tab going away should not wait on a retry backoff.
	if (trigger === "hidden") {
		flushOnHide(cfg);
		return;
	}

	if (flushing) return;

	const batch = queue.take(cfg.batchSize);
	if (batch.length === 0) return;

	flushing = true;
	try {
		const result = await send(eventsRequest(cfg, batch), `events x${batch.length} (${trigger})`, cfg);
		if (result === "exhausted") queue.requeue(batch);
	} finally {
		flushing = false;
	}
}

export const transport: Transport = {
	init(next: ResolvedConfig): void {
		scheduler?.stop();
		config = next;

		if (!enabled()) {
			queue = null;
			scheduler = null;
			logger.log("transport disabled: endpoint or apiKey is empty, or this is not a browser");
			return;
		}

		queue = new EventQueue(next.maxQueueSize);
		queue.restore();

		scheduler = new FlushScheduler({
			intervalMs: next.flushIntervalMs,
			onFlush: reason => void flush(reason),
		});
		scheduler.start();

		logger.log("transport ready", { endpoint: next.endpoint, batchSize: next.batchSize, flushIntervalMs: next.flushIntervalMs });

		if (queue.size > 0) void flush("init");
	},

	/** Never awaited and never gates events — the backend accepts events for an unregistered session. */
	startSession(session: AnalyticsSession, device: DeviceMetadata): void {
		if (!enabled() || config === null) return;
		currentSessionId = session.sessionId;
		void send(sessionRequest(config, session, device), "session start", config);
	},

	endSession(sessionId: string, endedAt: string): void {
		if (!enabled() || config === null) return;
		if (currentSessionId === sessionId) currentSessionId = null;
		void send(endSessionRequest(config, sessionId, endedAt), "session end", config);
	},

	enqueue(event: TrackedEvent): void {
		if (!enabled() || !queue || config === null) return;

		currentSessionId = event.sessionId;
		queue.push(event);
		if (queue.size >= config.batchSize) void flush("size");
	},
};
